import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { atividadesApi, type EstadoAtividade, type Atividade } from '../api/atividades.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';

const COLUNAS: EstadoAtividade[] = ['Pendente', 'EmAndamento', 'Bloqueada', 'Concluida', 'Cancelada'];

function CardAtividade({ atividade, onApontar }: { atividade: Atividade; onApontar: (horas: number) => void }) {
  return (
    <li className="rounded border border-borda bg-superficie p-3 text-sm">
      <p className="text-ink">{atividade.nome}</p>
      {atividade.responsavel && <p className="fonte-mono text-xs text-grafite">{atividade.responsavel}</p>}
      {atividade.bloqueioRef && <p className="text-xs text-status-vermelho">bloqueada por {atividade.bloqueioTipo}:{atividade.bloqueioRef}</p>}
      {/* SPEC-UX §4.6 — apontamento em 5 segundos: botão de horas no próprio card. */}
      <div className="mt-2 flex gap-1">
        <button type="button" onClick={() => onApontar(1)} className="rounded border border-aco/40 px-2 py-0.5 text-xs text-aco hover:bg-aco/5">
          +1h
        </button>
        <button type="button" onClick={() => onApontar(2)} className="rounded border border-aco/40 px-2 py-0.5 text-xs text-aco hover:bg-aco/5">
          +2h
        </button>
      </div>
    </li>
  );
}

// SPEC-UX §4.6 — kanban por fase: colunas = estados da máquina, sem coluna
// custom (§8 anti-padrão 2). "Apontar 2h <atividade>" via omnibox e o
// painel semanal fixável do Executor ficam para uma fatia futura.
export function AtividadesFase() {
  const { faseId } = useParams<{ faseId: string }>();
  const queryClient = useQueryClient();

  const { data: colunas, isLoading } = useQuery({
    queryKey: ['atividades', 'kanban', faseId],
    queryFn: () => atividadesApi.kanbanPorFase(faseId!),
    enabled: !!faseId,
  });

  const apontar = useMutation({
    mutationFn: ({ atividadeId, horas }: { atividadeId: string; horas: number }) =>
      atividadesApi.apontar(atividadeId, 'sistema', horas, new Date().toISOString().slice(0, 10)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['atividades', 'kanban', faseId] }),
  });

  const totalAtividades = colunas?.reduce((s, c) => s + c.atividades.length, 0) ?? 0;

  return (
    <div className="flex flex-col gap-6 p-8">
      <h1 className="text-xl font-semibold text-ink">Atividades</h1>

      {isLoading && <p className="text-sm text-grafite">Carregando…</p>}

      {!isLoading && totalAtividades === 0 && (
        <VazioQueEnsina explicacao="Nenhuma atividade nesta fase ainda. Atividades nascem do blueprint ou são criadas manualmente." />
      )}

      {colunas && totalAtividades > 0 && (
        <div className="flex gap-4 overflow-x-auto">
          {COLUNAS.map((estado) => {
            const coluna = colunas.find((c) => c.estado === estado);
            return (
              <div key={estado} className="w-56 flex-shrink-0">
                <h2 className="fonte-mono mb-2 text-xs font-semibold uppercase text-grafite">
                  {estado} ({coluna?.atividades.length ?? 0})
                </h2>
                <ul className="flex flex-col gap-2">
                  {coluna?.atividades.map((atividade) => (
                    <CardAtividade
                      key={atividade.id}
                      atividade={atividade}
                      onApontar={(horas) => apontar.mutate({ atividadeId: atividade.id, horas })}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
